import { gsap } from "../../animations/gsapSetup.ts";
import { DeviceMockup } from "../../components/common/DeviceMockup.tsx";
import { useGsapScrollTrigger } from "../../hooks/useGsapScrollTrigger.ts";
import { useIsMobile } from "../../hooks/useMediaQuery.ts";
import { useReducedMotion } from "../../hooks/useReducedMotion.ts";
import { useI18n } from "../../i18n/context.tsx";
import styles from "./HeartRateTimeline.module.css";

/* ── ECG waveform path (realistic QRS complex with P, T waves) ── */

function buildRealisticEcg(baseY: number, ampScale = 1): string {
  const parts = [`M-60,${baseY}`];
  // Each cycle ≈ 130px. 11 iterations covers −60 → ~1370 (past viewBox 1200)
  for (let i = 0; i < 11; i++) {
    const a = ampScale;
    const r = Math.round(55 * a); // R peak height (tall!)
    const p = Math.round(8 * a); // P wave height
    const s = Math.round(14 * a); // S dip depth
    const tw = Math.round(12 * a); // T wave height
    const q = Math.round(4 * a); // Q dip
    const rUp = -(r + q); // R delta from Q bottom
    const sDown = r + q + s; // S delta from R peak

    parts.push(
      // Baseline flat (wide spacing)
      "c8,0 15,0 22,0",
      // P wave: gentle bump up then back
      `c3,0 6,${-p} 10,${-p} c4,0 7,${p} 10,${p}`,
      // PR segment flat
      "c2,0 4,0 7,0",
      // Q dip
      `c0.4,0 1.2,${q} 2,${q}`,
      // R spike up (sharp)
      `c0.3,-1 1.2,${rUp + 2} 2,${rUp}`,
      // S drop below baseline
      `c0.4,${Math.round(-r * 0.06)} 2,${sDown - 2} 3,${sDown}`,
      // S recovery back to baseline (must cancel full q+s displacement)
      `c0.5,${Math.round(-(s + q) * 0.35)} 1.2,${-(s + q)} 2,${-(s + q)}`,
      // ST segment flat
      "c3,0 7,0 12,0",
      // T wave: broad gentle bump
      `c4,0 9,${-tw} 14,${-tw} c5,0 10,${tw} 14,${tw}`,
      // Trailing baseline (wide spacing)
      "c8,0 15,0 22,0",
    );
  }
  return parts.join(" ");
}

// All layers share same baseY=100 for parallel horizontal alignment
const ECG_MAIN = buildRealisticEcg(100, 1.0);
const ECG_BG1 = buildRealisticEcg(100, 0.6);
const ECG_BG2 = buildRealisticEcg(100, 0.75);

/* ── HR zone definitions ── */

interface HrZone {
  label: string;
  range: string;
  color: string;
  widthPct: number;
}

const HR_ZONES: HrZone[] = [
  { label: "Rest", range: "60–100", color: "#30d158", widthPct: 25 },
  { label: "Light", range: "100–120", color: "#64d2ff", widthPct: 25 },
  { label: "Cardio", range: "120–150", color: "#ffcc00", widthPct: 30 },
  { label: "Peak", range: "150–180+", color: "#ff3b30", widthPct: 20 },
];

// 142 BPM → in Cardio zone, ~70% of the way through the bar
const BPM_INDICATOR_PCT = 68;

export function HeartRateTimeline() {
  const reducedMotion = useReducedMotion();
  const isMobile = useIsMobile();
  const { t } = useI18n();

  const ref = useGsapScrollTrigger(
    (el) => {
      if (reducedMotion) return;

      const mainEcg = el.querySelector(`.${styles.ecgMain}`) as SVGPathElement | null;
      const bgEcg1 = el.querySelector(`.${styles.ecgBg1}`) as SVGPathElement | null;
      const bgEcg2 = el.querySelector(`.${styles.ecgBg2}`) as SVGPathElement | null;
      const glowOrb = el.querySelector(`.${styles.glowOrb}`);
      const header = el.querySelector(`.${styles.header}`);
      const mockup = el.querySelector(`.${styles.mockupWrap}`);
      const bpmNum = el.querySelector(`.${styles.bpmNumber}`) as HTMLElement | null;
      const zoneBar = el.querySelector(`.${styles.zoneBar}`);
      const indicator = el.querySelector(`.${styles.zoneIndicator}`);

      if (isMobile) {
        gsap.from([header, mockup, zoneBar], {
          y: 40,
          opacity: 0,
          duration: 0.8,
          stagger: 0.2,
          scrollTrigger: {
            trigger: el,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        });
        return;
      }

      /* ── ECG path draw (auto-play on enter) ── */
      const ecgPaths = [bgEcg1, bgEcg2, mainEcg];
      const ecgDurations = [2.5, 2.2, 2.0];
      const ecgDelays = [0, 0.15, 0.3];
      for (let i = 0; i < ecgPaths.length; i++) {
        const ecg = ecgPaths[i];
        if (!ecg) continue;
        const len = ecg.getTotalLength();
        gsap.set(ecg, { strokeDasharray: len, strokeDashoffset: len });
        gsap.to(ecg, {
          strokeDashoffset: 0,
          duration: ecgDurations[i],
          delay: ecgDelays[i],
          ease: "power1.inOut",
          scrollTrigger: {
            trigger: el,
            start: "top 75%",
            toggleActions: "play none none none",
          },
        });
      }

      /* ── Glow orb pulse ── */
      if (glowOrb) {
        gsap.to(glowOrb, {
          scale: 1.3,
          opacity: 0.8,
          duration: 0.8,
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut",
        });
      }

      /* ── Content reveal ── */
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: "top 60%",
          toggleActions: "play none none reverse",
        },
      });

      if (header) {
        gsap.set(header, { y: 30, opacity: 0 });
        tl.to(header, { y: 0, opacity: 1, duration: 0.8, ease: "power3.out" }, 0);
      }

      if (mockup) {
        gsap.set(mockup, { y: 40, opacity: 0, scale: 0.9 });
        tl.to(mockup, { y: 0, opacity: 1, scale: 1, duration: 1, ease: "power3.out" }, 0.2);
      }

      /* BPM counter */
      if (bpmNum) {
        const obj = { val: 0 };
        tl.to(
          obj,
          {
            val: 142,
            duration: 1.5,
            ease: "power2.out",
            onUpdate: () => {
              bpmNum.textContent = String(Math.round(obj.val));
            },
          },
          0.3,
        );
      }

      /* Zone bar + indicator */
      if (zoneBar) {
        gsap.set(zoneBar, { scaleX: 0, transformOrigin: "left center" });
        tl.to(zoneBar, { scaleX: 1, duration: 1, ease: "power2.out" }, 0.5);
      }
      if (indicator) {
        gsap.set(indicator, { opacity: 0, y: -10 });
        tl.to(indicator, { opacity: 1, y: 0, duration: 0.4, ease: "back.out(2)" }, 1.2);
      }
    },
    [isMobile],
  );

  return (
    <section ref={ref} className={styles.section}>
      {/* ── Background: layered ECG + glow ── */}
      <div className={styles.bgLayer}>
        <div className={styles.glowOrb} />
        <svg
          className={styles.ecgCanvas}
          viewBox="0 0 1200 200"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="hrEcgGrad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#ff3b30" />
              <stop offset="50%" stopColor="#ff6961" />
              <stop offset="100%" stopColor="#ff3b30" />
            </linearGradient>
          </defs>
          <path d={ECG_BG1} className={styles.ecgBg1} />
          <path d={ECG_BG2} className={styles.ecgBg2} />
          <path d={ECG_MAIN} className={styles.ecgMain} />
        </svg>
      </div>

      {/* ── Foreground content ── */}
      <div className={styles.content}>
        <div className={styles.header}>
          <h2 className={styles.title}>{t.ai.heartTitle}</h2>
          <p className={styles.subtitle}>{t.ai.heartDesc}</p>
        </div>

        <div className={styles.centerRow}>
          <div className={styles.mockupWrap}>
            <DeviceMockup device="watch">
              <div className={styles.hrDisplay}>
                <span className={styles.hrIcon}>{"\u2764\uFE0F"}</span>
                <span className={styles.bpmNumber}>142</span>
                <span className={styles.hrUnit}>{t.ai.heartBpm}</span>
              </div>
            </DeviceMockup>
          </div>
        </div>

        {/* ── HR Zone Bar ── */}
        <div className={styles.zoneSection}>
          <div className={styles.zoneBar}>
            {HR_ZONES.map((z) => (
              <div
                key={z.label}
                className={styles.zoneSegment}
                style={{ width: `${z.widthPct}%`, background: z.color }}
              />
            ))}
          </div>
          <div className={styles.zoneIndicator} style={{ left: `${BPM_INDICATOR_PCT}%` }}>
            <div className={styles.indicatorLine} />
            <span className={styles.indicatorLabel}>142 BPM</span>
          </div>
          <div className={styles.zoneLabels}>
            {HR_ZONES.map((z) => (
              <div
                key={z.label}
                className={styles.zoneLabelItem}
                style={{ width: `${z.widthPct}%` }}
              >
                <span className={styles.zoneName} style={{ color: z.color }}>
                  {z.label}
                </span>
                <span className={styles.zoneRange}>{z.range}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
